import { useAuth0 } from "@auth0/auth0-react";
import { useCallback } from "react";

const BASE_URL = import.meta.env.VITE_API_BASE_URL || "";

export function getPublicApiUrl(path: string): string {
  return `${BASE_URL}/api/v1${path}`;
}

export function useApiClient() {
  const { getAccessTokenSilently } = useAuth0();

  return useCallback(
    async <T = unknown>(path: string, options?: RequestInit): Promise<T> => {
      const token = await getAccessTokenSilently();
      const res = await fetch(`${BASE_URL}/api/v1${path}`, {
        ...options,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          ...options?.headers,
        },
      });

      if (!res.ok) {
        throw await createApiError(res);
      }

      return res.json();
    },
    [getAccessTokenSilently],
  );
}

export function usePublicApiClient() {
  return useCallback(async <T = unknown>(path: string): Promise<T> => {
    const res = await fetch(`${BASE_URL}/api/v1${path}`);
    if (!res.ok) {
      throw new ApiError(res.status, `API error: ${res.status}`, "UNKNOWN");
    }
    return res.json();
  }, []);
}

/**
 * API client that sends the auth token when available (for tier-gated routes).
 * Falls back to unauthenticated requests when the user is not logged in.
 */
export function useOptionalAuthApiClient() {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();

  return useCallback(
    async <T = unknown>(path: string): Promise<T> => {
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };

      if (isAuthenticated) {
        try {
          const token = await getAccessTokenSilently();
          headers.Authorization = `Bearer ${token}`;
        } catch {
          // Token retrieval failed — continue as anonymous
        }
      }

      const res = await fetch(`${BASE_URL}/api/v1${path}`, { headers });
      if (!res.ok) {
        throw new ApiError(res.status, `API error: ${res.status}`, "UNKNOWN");
      }
      return res.json();
    },
    [isAuthenticated, getAccessTokenSilently],
  );
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public code: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

type ApiErrorBody = {
  errors?: Array<{ code?: string; message?: string }>;
  error?: {
    message?: string;
    issues?: Array<{ message?: string; path?: Array<string | number> }>;
  };
  message?: string;
};

async function createApiError(response: Response): Promise<ApiError> {
  const body = (await response.json().catch(() => null)) as ApiErrorBody | null;
  const standardError = body?.errors?.[0];
  const validationIssue = body?.error?.issues?.[0];
  const validationPath = validationIssue?.path?.join(".");
  const validationMessage = validationIssue?.message
    ? `${validationPath ? `${validationPath}: ` : ""}${validationIssue.message}`
    : undefined;

  return new ApiError(
    response.status,
    standardError?.message ??
      validationMessage ??
      body?.error?.message ??
      body?.message ??
      `API error: ${response.status}`,
    standardError?.code ?? (validationIssue ? "BAD_REQUEST" : "UNKNOWN"),
  );
}
